/**
 * upload-or-enqueue.ts — resilient photo upload for the panel fotográfico.
 *
 * Uploads a photo via uploadImagen; when the failure is a network or 5xx error
 * (isNetworkOrServerFailure), the photo is enqueued as an IDB blob with an
 * `idb-blob:` placeholder instead of being lost — the blob-queue IS the retry
 * mechanism (flush re-uploads with backoff on online/visible/manual).
 *
 * Branching is ALWAYS on the actual error, never on navigator.onLine. The only
 * role of navigator.onLine is its existing fast-path: when false, skip the
 * doomed upload attempt and enqueue directly.
 *
 * 4xx errors are client errors: surfaced to the user, NEVER enqueued.
 */
import { friendlyMessage, isNetworkOrServerFailure } from "@/shared/lib/errors.js";
import { uploadImagen } from "@/shared/lib/upload.js";
import { compressImage } from "@/shared/lib/compress-image.js";
import { enqueueBlob, getBlobsForReport } from "@/features/offline-sync/services/blob-queue.js";
import { run as flushRun } from "@/features/offline-sync/services/flush.js";

export type ProcesarFotoResult =
  | { kind: "uploaded"; url: string }
  | { kind: "enqueued"; url: string } // idb-blob: placeholder
  | { kind: "error"; message: string };

export const MAX_PHOTO_BYTES = 5_000_000;

/**
 * Validation gates that run on the COMPRESSED photo: file-type gate first
 * (on the original, before any decode work), then compression, then the 5 MB
 * gate on the final bytes that will actually be uploaded/enqueued.
 * Fail-open: an undecodable photo comes back from compressImage unchanged and
 * the size gate still applies to the original bytes.
 */
export async function preparePhotoFile(
  file: File,
): Promise<{ kind: "ok"; file: File } | { kind: "error"; message: string }> {
  if (!file.type.startsWith("image/")) {
    return { kind: "error", message: "El archivo debe ser una imagen." };
  }
  const compressed = await compressImage(file);
  if (compressed.size > MAX_PHOTO_BYTES) {
    return { kind: "error", message: "La imagen no debe superar 5 MB." };
  }
  return { kind: "ok", file: compressed };
}

/** Enqueues a photo File as an IDB blob; returns the idb-blob: placeholder URL. */
async function enqueuePhoto(file: File, localId: string): Promise<string> {
  const fieldPath = `panelFotografico.${crypto.randomUUID()}`;
  const blobId = `${localId}:${fieldPath}`;
  await enqueueBlob({
    id: blobId,
    ownerLocalId: localId,
    fieldPath,
    blob: file,
    contentType: file.type || "image/jpeg",
  });
  // ponytail: placeholder replaced by flush with the real R2 URL on reconnect.
  return `idb-blob:${blobId}`;
}

/**
 * Uploads a photo, falling back to the offline blob queue on network/5xx failure.
 * When offline, skips the upload attempt entirely and enqueues directly.
 */
export async function uploadOrEnqueue(file: File, localId: string): Promise<ProcesarFotoResult> {
  if (!navigator.onLine) {
    return { kind: "enqueued", url: await enqueuePhoto(file, localId) };
  }
  try {
    const url = await uploadImagen(file, "informes");
    return { kind: "uploaded", url };
  } catch (err) {
    if (isNetworkOrServerFailure(err)) {
      return { kind: "enqueued", url: await enqueuePhoto(file, localId) };
    }
    return { kind: "error", message: friendlyMessage(err) };
  }
}

/**
 * Manual retry: a single-flight flush drains queued blobs AND syncs the report —
 * the only path that resolves placeholders end-to-end.
 */
export async function retryQueuedPhotos(localId: string): Promise<void> {
  void localId; // flush drains all pending blobs; single-flight makes repeat taps safe
  await flushRun("manual");
}

/**
 * Per-photo state after a retry run, derived from the blob's uploadState.
 * `missing` = the blob is gone from IDB (e.g. discarded after a successful sync)
 * — the placeholder can no longer resolve from this queue.
 */
export async function blobStateForPlaceholder(
  placeholderUrl: string,
): Promise<"uploaded" | "pending" | "missing"> {
  // Placeholder shape: idb-blob:${ownerLocalId}:${fieldPath}
  const withoutScheme = placeholderUrl.slice("idb-blob:".length);
  const sep = withoutScheme.indexOf(":");
  if (sep <= 0 || sep === withoutScheme.length - 1) return "missing";
  const ownerLocalId = withoutScheme.slice(0, sep);
  const fieldPath = withoutScheme.slice(sep + 1);
  const blobs = await getBlobsForReport(ownerLocalId);
  const blob = blobs.find((b) => b.fieldPath === fieldPath);
  if (!blob) return "missing";
  return blob.uploadState === "uploaded" ? "uploaded" : "pending";
}
